import type { MechanicDefinition } from "./types";

/* ── Preset narrative skins ── */
const PRESET_FRAMES: Record<string, { frame: string; locationLabel: string; personnelLabel: string; description: string }> = {
  criminal: { frame: "Criminal Syndicate", locationLabel: "Turf / Racket", personnelLabel: "Crew / Muscle", description: "Gang, mafia, cartel — illicit operations, territory wars, heat management." },
  military: { frame: "Military Command", locationLabel: "Base / Outpost", personnelLabel: "Troops / Officers", description: "Army, navy, mercenary company — troop deployment, logistics, campaigns." },
  corporate: { frame: "Corporate Empire", locationLabel: "Office / Branch", personnelLabel: "Staff / Executives", description: "Business empire, hostile takeovers, market manipulation, lobbying." },
  feudal: { frame: "Kingdom / Domain", locationLabel: "Province / Fief", personnelLabel: "Subjects / Knights", description: "Medieval domain — vassals, fiefdoms, fealty, tribute." },
  settlement: { frame: "Colony / Settlement", locationLabel: "District / Zone", personnelLabel: "Residents / Workers", description: "City builder — districts, infrastructure, population management." },
  custom: { frame: "Custom Organization", locationLabel: "Location", personnelLabel: "Personnel", description: "Define your own organizational archetype." },
};

const STYLE_DESCRIPTIONS: Record<string, string> = {
  hierarchical: "Hierarchical chain of command. Orders flow down, reports flow up. Clear ranks.",
  cell: "Cell-based structure. Independent cells with handlers. Compartmentalized — secure but slow.",
  feudal: "Feudal structure. Territory lords swear fealty. Autonomous operations with tribute obligations.",
};

export const strategyOperations: MechanicDefinition = {
  id: "strategy_operations",
  name: "Strategy & Operations",
  description: "Unified management of organizations, territory control, resources, and workforce — with narrative presets for different game styles",
  icon: "Building2",
  category: "systems",
  subGroup: "strategy",
  aiHints: {
    purpose: "Single hub for org management, territory control, abstract resources, and personnel/workforce tracking. Uses location entities for territory, manpower_roster for personnel, resource_pools for abstract metrics, and inventory for physical commodities.",
    whenToUse: "Games with leadership, conquest, management, strategy, or any world where the player controls an organization, territory, or workforce.",
    synergies: ["inventory", "economy", "world_map", "region_movement", "reputation", "influence"],
    conflicts: [],
    prerequisites: [],
    tags: ["organization", "territory", "resources", "management", "strategy", "workforce", "manpower", "conquest", "leadership"],
    complexity: 4,
    exampleConfig: { preset: "criminal", heat_system: true, enable_manpower: true, rival_factions: true },
    stateVariables: [
      "org_name", "org_tier", "org_morale", "org_heat", "org_income_per_turn",
      "controlled_locations", "contested_locations", "rival_factions",
      "manpower_roster", "resource_pools", "resource_production", "resource_upkeep", "_resource_net",
      "_territory_controlled_count", "_territory_contested_count",
    ],
  },

  configSchema: [
    {
      key: "preset",
      label: "Preset",
      type: "select",
      options: [
        { value: "criminal", label: "Criminal Syndicate", description: "Gang, mafia, cartel — illicit operations, territory wars, heat management" },
        { value: "military", label: "Military Command", description: "Army, navy, mercenary company — troop deployment, logistics, campaigns" },
        { value: "corporate", label: "Corporate Empire", description: "Business empire, hostile takeovers, market manipulation, lobbying" },
        { value: "feudal", label: "Kingdom / Domain", description: "Medieval domain — vassals, fiefdoms, fealty, tribute" },
        { value: "settlement", label: "Colony / Settlement", description: "City builder — districts, infrastructure, population management" },
        { value: "custom", label: "Custom", description: "Define your own organizational archetype" },
      ],
      default: "criminal",
    },
    {
      key: "org_name_default",
      label: "Default Organization Name",
      type: "text",
      default: "",
      placeholder: "The Syndicate",
    },
    {
      key: "management_style",
      label: "Management Style",
      type: "select",
      options: [
        { value: "hierarchical", label: "Hierarchical", description: "Chain of command — clear rank structure." },
        { value: "cell", label: "Cell-based", description: "Independent cells with handlers. Secure but slow." },
        { value: "feudal", label: "Feudal", description: "Territory lords swear fealty. Autonomous but loyal (or not)." },
      ],
      default: "hierarchical",
    },
    {
      key: "starting_tier",
      label: "Starting Tier",
      type: "select",
      options: [
        { value: "1", label: "Tier 1 — Starting Out", description: "Small crew, local operations, scraping by" },
        { value: "2", label: "Tier 2 — Established", description: "Known name, some territory, steady income" },
        { value: "3", label: "Tier 3 — Major Player", description: "Significant power, multiple territories, feared/respected" },
      ],
      default: "1",
    },
    { key: "heat_system", label: "Heat / Attention System", type: "boolean", default: true, description: "Track law enforcement / rival attention. High heat triggers raids and crackdowns." },
    {
      key: "control_mechanic",
      label: "Territory Control Mechanic",
      type: "select",
      options: [
        { value: "influence", label: "Influence", description: "Soft power — bribe, persuade, install loyalists." },
        { value: "military", label: "Military", description: "Force — send troops, fight defenders." },
        { value: "economic", label: "Economic", description: "Buy out — purchase property, monopolize trade." },
      ],
      default: "military",
    },
    { key: "rival_factions", label: "Rival Factions", type: "boolean", default: true, description: "AI generates competing factions that contest territory." },
    {
      key: "territory_scale",
      label: "Territory Scale",
      type: "select",
      options: [
        { value: "neighborhoods", label: "Neighborhoods", description: "City-block level" },
        { value: "districts", label: "Districts", description: "City-wide" },
        { value: "regions", label: "Regions", description: "Country-level" },
        { value: "planets", label: "Planets", description: "Galactic scale" },
      ],
      default: "districts",
    },
    { key: "territory_income", label: "Territory Income", type: "boolean", default: true, description: "Controlled locations generate resources each turn" },
    {
      key: "seed_resources",
      label: "Starting Resource Suggestions",
      type: "text",
      default: "",
      placeholder: "troops, materials, intel, food",
      description: "Optional comma-separated resource names. Leave empty — the AI creates resources contextually.",
    },
    { key: "production_buildings", label: "Production from Locations", type: "boolean", default: true, description: "Controlled locations can produce resources each turn" },
    { key: "upkeep", label: "Resource Upkeep", type: "boolean", default: true, description: "Resources cost upkeep per turn" },
    { key: "enable_manpower", label: "Manpower / Personnel Tracking", type: "boolean", default: true, description: "Track personnel batches with counts, experience, and optional NPC links." },
  ],

  systemPromptSection: (config) => {
    const preset = config.preset || "criminal";
    const pf = PRESET_FRAMES[preset] || PRESET_FRAMES.custom;
    const style = config.management_style || "hierarchical";
    const heat = config.heat_system !== false;
    const tier = parseInt(config.starting_tier || "1");
    const control = config.control_mechanic || "military";
    const rivals = config.rival_factions !== false;
    const income = config.territory_income !== false;
    const scale = config.territory_scale || "districts";
    const buildings = config.production_buildings !== false;
    const upkeep = config.upkeep !== false;
    const manpower = config.enable_manpower !== false;
    const seedStr = config.seed_resources || "";
    const seeds = seedStr.split(",").map((s: string) => s.trim()).filter(Boolean);

    return `## STRATEGY & OPERATIONS (Rules — Unified Hub)
- Preset: ${pf.frame} — ${pf.description}
- Locations referred to as: ${pf.locationLabel}
- Personnel referred to as: ${pf.personnelLabel}
- Structure: ${STYLE_DESCRIPTIONS[style]}
- Current Tier: ${tier}/5

### CROSS-MECHANIC INTEGRATION — CRITICAL
- **Inventory**: Org physical assets use \`manage_inventory\` with \`inventory_scope="org"\`. Do NOT track org items separately.
- **Economy**: Org treasury IS the economy currency (currency_amount). \`collect_income\` adds income via \`transaction(type="reward")\`.
- **Personnel**: All personnel are \`manpower_roster\` batches. Do NOT duplicate in separate lists.
- **Territory**: Territory = location entities with \`controlled_by\` property. Use \`modify_entity\` to tag ownership.
- **Resources**: Abstract metrics (influence, morale_pool) use \`resource_pools\`. Physical commodities (gold, salt) use inventory.

### ORGANIZATION STATE
- org_name, org_tier (1-5), org_morale (0-100), org_income_per_turn
- org_controlled_locations → USE controlled_locations[] instead (unified)
${heat ? `
### HEAT SYSTEM (0-100)
- org_heat tracks attention from law enforcement / rivals.
- 0-20: Under the radar | 21-40: Noticed | 41-60: Targeted | 61-80: Hunted | 81-100: Full crackdown
- Heat INCREASES from: violent ops, public crimes, expanding too fast.
- Heat DECREASES from: laying low, bribing officials, time passing.
- postTurnHook: heat decays 2-5 per turn naturally.` : ""}

### TIER ADVANCEMENT
- Tier 1→2: Control 2+ locations, 50+ total resources, complete a significant operation.
- Tier 2→3: Control 5+ locations, 200+ resources, establish a signature operation.
- Tier 3→4: Control 10+ locations, 500+ resources, dominate a region.
- Tier 4→5: Control an entire region, 1000+ resources, become undisputed power.

### MORALE EFFECTS
- Morale > 80: Bonus to operations, loyal members, recruitment easier.
- Morale 40-80: Normal operations.
- Morale < 40: Members grumble, desertion risk. < 20: Mutiny risk.

### COMMANDER AGENTS
- Appoint commanders via create_npc(role="commander"). Tag entity with modify_entity(properties={role:"commander"}).
- Delegate tasks to commanders via propose_quest(assigned_to=commander_entity_id).

## TERRITORY CONTROL (Uses Location Entities)
- Scale: ${scale} — each territory is an existing LOCATION entity.
- Primary control mechanic: ${control === "influence" ? "Influence — bribe, persuade, install loyalists." : control === "economic" ? "Economic — buy property, monopolize trade." : "Military — send troops, fight defenders."}
- Use claim_location to attempt control. Use defend_location to assign defenses.
- After claim_location succeeds, ALWAYS call modify_entity(entity_id=location_id, properties={controlled_by:"player", defense_level:1}).
- Territories tracked in controlled_locations[] and contested_locations[].
${rivals ? `
### RIVAL FACTIONS
- Competing factions claim territory, expand, defend, and retaliate.
- rival_factions[] tracks competitors with territories and strength.
- Rivals attack weakly defended territories between turns.` : ""}
${income ? `
### TERRITORY INCOME
- Controlled locations generate resources: Commercial→currency, Industrial→materials, Residential→recruitment, Strategic→intel.
- Income processed via resource production system.` : ""}

## RESOURCES (Dynamic)
- Resources are DYNAMIC — create types as the narrative demands.
${seeds.length > 0 ? `- Suggested starting resources: ${seeds.join(", ")}` : "- No predefined resources. Create contextually."}
- Use adjust_resource to add/subtract. Creates the type if new.
- resource_pools: { [type]: { amount, icon, description, tags[] } }
- ABSTRACT metrics (morale, influence, political_capital) → resource_pools
- PHYSICAL commodities (gold bars, salt, ammo) → inventory items with quantity
${buildings ? `
### PRODUCTION
- Controlled locations produce resources each turn via set_production.
- Production happens automatically in postTurnHook.` : ""}
${upkeep ? `
### UPKEEP
- Resources have maintenance costs per turn.
- If upkeep cannot be paid, resources degrade: troops desert, buildings decay.` : ""}

### DEPLETION
- When a resource hits 0: narrative consequences follow (troops can't fight, trade halts, etc.)
${manpower ? `
## MANPOWER / PERSONNEL
- Track personnel as batches in manpower_roster: { batch_id: { role, count, experience, icon, tags[], linked_npc_ids[], status, assigned_location_id } }
- Use manage_manpower to recruit, dismiss, assign, promote, or link NPCs.
- Linked NPCs retain individuality but are counted within the batch.
- Personnel consume resources per turn (pay, supplies). Depletion → desertion.
- Population demographics on locations: model as batches with assigned_location_id.` : ""}`;
  },

  widgets: [
    {
      id: "ops_status",
      type: "status_bar_item",
      label: "Operations",
      icon: "Building2",
      component: "OperationsStatusWidget",
      dataBindings: {
        orgName: "variables.org_name",
        orgTier: "variables.org_tier",
        orgMorale: "variables.org_morale",
        orgHeat: "variables.org_heat",
        orgMembers: "variables.manpower_roster",
        controlledCount: "variables._territory_controlled_count",
        contestedCount: "variables._territory_contested_count",
        resourcePools: "variables.resource_pools",
      },
      visibility: "when_data",
      priority: 5,
    },
    {
      id: "ops_panel",
      type: "sidebar_tab",
      label: "Operations",
      icon: "Building2",
      component: "OperationsPanel",
      dataBindings: {
        // Org
        orgName: "variables.org_name",
        orgTier: "variables.org_tier",
        orgMorale: "variables.org_morale",
        orgHeat: "variables.org_heat",
        orgIncome: "variables.org_income_per_turn",
        managementStyle: "variables._management_style",
        currencyAmount: "variables.currency_amount",
        currencyName: "variables.currency_name",
        // Territory
        controlledLocations: "variables.controlled_locations",
        contestedLocations: "variables.contested_locations",
        rivalFactions: "variables.rival_factions",
        territoryScale: "variables._territory_scale",
        // Resources
        resourcePools: "variables.resource_pools",
        resourceProduction: "variables.resource_production",
        resourceUpkeep: "variables.resource_upkeep",
        resourceNet: "variables._resource_net",
        // Manpower
        manpowerRoster: "variables.manpower_roster",
      },
      visibility: "always",
      priority: 30,
      actions: [
        { id: "ops_status_report", label: "Status Report", icon: "BarChart3", messageTemplate: "Give me a full operations status report." },
        { id: "org_upgrade", label: "Upgrade Tier", icon: "ArrowUpCircle", messageTemplate: "I want to upgrade the organization to the next tier." },
        { id: "collect_income", label: "Collect Income", icon: "Coins", messageTemplate: "Collect all income from our operations and territories." },
        { id: "claim_territory", label: "Claim Territory", icon: "Flag", messageTemplate: "I want to claim control of {{location_name}}." },
        { id: "defend_territory", label: "Fortify", icon: "Shield", messageTemplate: "Fortify defenses at {{location_name}}." },
        { id: "recruit", label: "Recruit", icon: "UserPlus", messageTemplate: "I want to recruit new members for the organization." },
      ],
    },
  ],

  initializeState: (config) => {
    const tier = parseInt(config.starting_tier || "1");
    const seedStr = config.seed_resources || "";
    const seeds = seedStr.split(",").map((s: string) => s.trim()).filter(Boolean);
    const pools: Record<string, { amount: number; icon: string; description: string; tags: string[] }> = {};
    for (const s of seeds) {
      pools[s] = { amount: 0, icon: "📦", description: "", tags: [] };
    }

    const base: Record<string, any> = {
      // Org
      org_name: config.org_name_default || "",
      _org_preset: config.preset || "criminal",
      _management_style: config.management_style || "hierarchical",
      org_tier: tier,
      org_morale: 60,
      org_heat: config.heat_system !== false ? 10 : 0,
      org_income_per_turn: 0,
      // Territory
      controlled_locations: [],
      contested_locations: [],
      _territory_scale: config.territory_scale || "districts",
      _territory_controlled_count: 0,
      _territory_contested_count: 0,
      // Resources
      resource_pools: pools,
      resource_production: {},
      resource_upkeep: {},
      _resource_net: {},
    };

    if (config.rival_factions !== false) base.rival_factions = [];
    if (config.enable_manpower !== false) base.manpower_roster = {};

    return base;
  },

  onEvent: (event) => {
    // Territory events
    if (event.type === "territory_claimed") {
      return {
        narrativeNotes: [
          "🏢 New territory under control — income and influence increased.",
          `📦 Territory "${event.data.location_name || ""}" claimed — consider assigning resource production via set_production.`,
        ],
      };
    }
    if (event.type === "territory_lost") {
      return {
        narrativeNotes: [
          "🏢 Territory lost — income reduced, morale impact.",
          `📦 Production from "${event.data.location_name || event.data.location_id}" halted.`,
        ],
      };
    }
    if (event.type === "region_enter" && event.data.region_id) {
      return {
        narrativeNotes: ["🗺️ Entering territory — check if controlled, contested, or unclaimed."],
      };
    }
    if (event.type === "quest_complete" && event.data.category === "operation") {
      return { narrativeNotes: ["🏢 Operation completed — org reputation and morale boosted."] };
    }
    if (event.type === "combat_kill") {
      const notes = ["🏢 Combat engagement — heat may increase from violence."];
      if (event.data.context === "territory_defense") {
        notes.push("🗺️ Territory defense combat — update defender strength and control status.");
      }
      return { narrativeNotes: notes };
    }
    return null;
  },

  postTurnHook: (context) => {
    const vars = context.state.variables || {};
    const updates: Record<string, any> = {};
    const notes: string[] = [];
    let changed = false;

    // ── Org: Heat decay ──
    const heat = vars.org_heat ?? 0;
    if (heat > 0 && context.config.heat_system !== false) {
      const decay = heat > 60 ? 2 : heat > 30 ? 3 : 5;
      updates.org_heat = Math.max(0, heat - decay);
      changed = true;
    }

    // ── Org: Morale drift toward 50 ──
    const morale = vars.org_morale ?? 60;
    if (morale > 55) { updates.org_morale = morale - 1; changed = true; }
    else if (morale < 45) { updates.org_morale = morale + 1; changed = true; }

    if (morale < 20) notes.push("⚠️ Organization morale critically low! Mutiny and desertion risk.");
    else if (morale < 40) notes.push("⚠️ Organization morale is declining. Members are restless.");
    if (heat > 80) notes.push("🚨 Heat CRITICAL — full crackdown imminent.");
    else if (heat > 60) notes.push("🔥 Heat is high — raids and investigations likely.");

    // ── Territory: Update counts ──
    const controlled = vars.controlled_locations || [];
    const contested = vars.contested_locations || [];
    updates._territory_controlled_count = controlled.length;
    updates._territory_contested_count = contested.length;
    if (contested.length > 0) notes.push(`⚔️ ${contested.length} territory/territories contested — rivals may attack.`);

    // Supply line awareness
    const tradeRoutes = vars.trade_routes || [];
    const blockaded = vars.blockaded_routes || [];
    if (tradeRoutes.length > 0) {
      const activeBlockades = blockaded.filter((b: any) =>
        tradeRoutes.some((tr: any) =>
          (tr.from === b.from && tr.to === b.to) || (tr.from === b.to && tr.to === b.from)
        )
      );
      if (activeBlockades.length > 0) notes.push(`🚫 ${activeBlockades.length} trade route(s) blockaded.`);
    }
    if (context.config.rival_factions !== false && controlled.length > 3) {
      notes.push("🗺️ Large territory holding — rivals more likely to coordinate against you.");
    }

    // ── Resources: Production & Upkeep ──
    const pools: Record<string, any> = {};
    for (const [k, v] of Object.entries(vars.resource_pools || {})) {
      pools[k] = { ...(v as any) };
    }
    const production: Record<string, any> = vars.resource_production || {};
    const upkeepMap: Record<string, any> = vars.resource_upkeep || {};

    for (const [, prod] of Object.entries(production) as [string, any][]) {
      if (prod?.type && prod?.amount && pools[prod.type]) {
        pools[prod.type].amount = (pools[prod.type].amount || 0) + prod.amount;
        changed = true;
      }
    }

    if (context.config.upkeep !== false) {
      for (const [resType, cost] of Object.entries(upkeepMap) as [string, number][]) {
        if (typeof cost === "number" && cost > 0 && pools[resType]) {
          const prev = pools[resType].amount || 0;
          pools[resType].amount = Math.max(0, prev - cost);
          if (prev - cost < 0) notes.push(`⚠️ ${resType} deficit! Upkeep exceeds supply.`);
          changed = true;
        }
      }
    }

    const netMap: Record<string, number> = {};
    for (const [resType] of Object.entries(pools)) {
      const prodVal = Object.values(production).filter((p: any) => p?.type === resType).reduce((s: number, p: any) => s + (p?.amount || 0), 0);
      const upk = typeof upkeepMap[resType] === "number" ? upkeepMap[resType] : 0;
      netMap[resType] = prodVal - upk;
      const amt = typeof pools[resType]?.amount === "number" ? pools[resType].amount : 0;
      if (amt === 0) notes.push(`⚠️ ${resType} DEPLETED!`);
      else if (amt > 0 && amt <= 5) notes.push(`⚠️ ${resType} critically low: ${amt} remaining.`);
    }

    // Manpower notes
    const roster = vars.manpower_roster || {};
    for (const [id, batch] of Object.entries(roster) as [string, any][]) {
      if (batch && typeof batch.count === "number" && batch.count === 0) {
        notes.push(`👷 ${batch.role || id} — no personnel remaining!`);
      }
    }

    if (changed) {
      updates.resource_pools = pools;
    }
    updates._resource_net = netMap;

    if (!changed && notes.length === 0 && !updates._territory_controlled_count) return null;
    return {
      stateUpdates: updates,
      narrativeNotes: notes.length > 0 ? notes : undefined,
    };
  },

  tools: [
    // ── Organization tools ──
    {
      type: "function",
      function: {
        name: "org_upgrade",
        description: "Upgrade the organization's tier, invest in infrastructure, or rename the org",
        parameters: {
          type: "object",
          properties: {
            action: { type: "string", enum: ["tier_up", "invest", "rename"], description: "Type of upgrade" },
            investment_type: { type: "string", description: "For invest: what to build (safe_house, barracks, lab, armory, etc.)" },
            new_name: { type: "string", description: "For rename: new organization name" },
            cost_description: { type: "string", description: "What resources/currency this costs" },
          },
          required: ["action"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "collect_income",
        description: "Trigger income collection from all controlled territories and operations",
        parameters: {
          type: "object",
          properties: {
            bonus_reason: { type: "string", description: "Optional: why there's a bonus or penalty this turn" },
          },
          required: [],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "org_status",
        description: "Generate a full organization status report including tier, morale, heat, territories, income, and personnel",
        parameters: { type: "object", properties: {}, required: [] },
      },
    },
    {
      type: "function",
      function: {
        name: "adjust_heat",
        description: "Increase or decrease organization heat (law enforcement / rival attention)",
        parameters: {
          type: "object",
          properties: {
            change: { type: "number", description: "Positive increases heat, negative decreases" },
            reason: { type: "string", description: "What caused the heat change" },
          },
          required: ["change", "reason"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "adjust_morale",
        description: "Change organization morale",
        parameters: {
          type: "object",
          properties: {
            change: { type: "number", description: "Positive boosts morale, negative reduces it" },
            reason: { type: "string", description: "What caused the morale change" },
          },
          required: ["change", "reason"],
        },
      },
    },
    // ── Territory tools ──
    {
      type: "function",
      function: {
        name: "claim_location",
        description: "Attempt to take control of a location entity",
        parameters: {
          type: "object",
          properties: {
            location_id: { type: "string", description: "Entity ID of the location" },
            location_name: { type: "string", description: "Human-readable name" },
            method: { type: "string", enum: ["military", "influence", "economic", "stealth"], description: "How to take control" },
            resources_committed: { type: "string", description: "What resources are being used" },
            current_controller: { type: "string", description: "Who currently controls it" },
          },
          required: ["location_id", "location_name", "method"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "defend_location",
        description: "Assign resources to defend a controlled location",
        parameters: {
          type: "object",
          properties: {
            location_id: { type: "string" },
            location_name: { type: "string" },
            defense_resources: { type: "string", description: "Resources assigned" },
            defense_level: { type: "number", description: "1-5 defense strength" },
          },
          required: ["location_id", "location_name", "defense_level"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "territory_status",
        description: "Report on all controlled and contested territories, rival positions, and strategic overview",
        parameters: { type: "object", properties: {}, required: [] },
      },
    },
    {
      type: "function",
      function: {
        name: "sabotage_location",
        description: "Undermine a rival's control of a location",
        parameters: {
          type: "object",
          properties: {
            location_id: { type: "string" },
            location_name: { type: "string" },
            method: { type: "string", enum: ["sabotage", "espionage", "subversion", "propaganda"] },
            resources_used: { type: "string" },
          },
          required: ["location_id", "location_name", "method"],
        },
      },
    },
    // ── Resource tools ──
    {
      type: "function",
      function: {
        name: "adjust_resource",
        description: "Add or subtract an ABSTRACT resource (morale, influence, political capital). For physical commodities use manage_inventory. Creates the resource type if new.",
        parameters: {
          type: "object",
          properties: {
            resource_type: { type: "string", description: "Resource name. Created if new." },
            amount: { type: "number", description: "Positive to add, negative to subtract" },
            reason: { type: "string", description: "Why the resource changed" },
            icon: { type: "string", description: "Emoji icon for new resource" },
            description: { type: "string", description: "Brief description for new resource" },
            tags: { type: "array", items: { type: "string" } },
          },
          required: ["resource_type", "amount", "reason"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "set_production",
        description: "Assign a controlled location to produce a specific resource each turn",
        parameters: {
          type: "object",
          properties: {
            location_id: { type: "string" },
            location_name: { type: "string" },
            resource_type: { type: "string" },
            amount_per_turn: { type: "number" },
          },
          required: ["location_id", "location_name", "resource_type", "amount_per_turn"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "establish_trade_route",
        description: "Designate a trade route between two locations for production bonuses",
        parameters: {
          type: "object",
          properties: {
            from: { type: "string" },
            to: { type: "string" },
            resource_bonus: { type: "string" },
          },
          required: ["from", "to"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "blockade_route",
        description: "Block a trade/supply route",
        parameters: {
          type: "object",
          properties: {
            from: { type: "string" },
            to: { type: "string" },
            method: { type: "string" },
            resources_committed: { type: "string" },
          },
          required: ["from", "to", "method"],
        },
      },
    },
    // ── Manpower tools ──
    {
      type: "function",
      function: {
        name: "manage_manpower",
        description: "Recruit, dismiss, assign, promote, or link NPC entities to personnel batches",
        parameters: {
          type: "object",
          properties: {
            action: { type: "string", enum: ["recruit", "dismiss", "promote", "link_npc", "unlink_npc", "update"] },
            batch_id: { type: "string" },
            role: { type: "string" },
            count: { type: "number" },
            experience: { type: "string" },
            icon: { type: "string" },
            tags: { type: "array", items: { type: "string" } },
            npc_entity_id: { type: "string" },
            status: { type: "string" },
            assigned_location_id: { type: "string", description: "Location entity ID where this batch is stationed" },
            reason: { type: "string" },
          },
          required: ["action", "batch_id"],
        },
      },
    },
  ],
};
